import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Filter } from "lucide-react";
import { useLegal } from "components/app/LegalModal";
import { serverFiles, formatPersonName, splitProfileIndustries } from "utils/utils";
import useResolvedLocation from "hooks/useResolvedLocation";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const initialsOf = (profile) => {
  if (profile?.userType === "BUSINESS") {
    const name = profile?.companyResponseDto?.name || "Q";
    return name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }
  const name = formatPersonName(profile?.firstName, profile?.lastName);
  if (!name) return "Q";
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

const FOOTER_LINKS = [
  { label: "About", key: "about" },
  { label: "Contact us", key: "contact" },
  { label: "Terms", key: "terms" },
  { label: "Privacy Policy", key: "privacy" },
  { label: "Community guidelines", key: "community" },
];

const normalizeFilters = (filters) =>
  (filters || []).map((f) =>
    typeof f === "string" ? { label: f, value: f } : f
  );

export const LeftSidebar = ({
  filters,
  currentFilter,
  onFilterChange,
  filterTitle = "Filter feed",
}) => {
  const { userProfile } = useSelector((state) => state.userProfile);
  const legal = useLegal();
  const location = useResolvedLocation(userProfile);

  const displayName =
    userProfile?.userType === "BUSINESS"
      ? userProfile?.companyResponseDto?.name
      : formatPersonName(userProfile?.firstName, userProfile?.lastName);

  const isBrand = userProfile?.userType === "BUSINESS";
  const rawIndustry =
    userProfile?.industry ||
    userProfile?.companyResponseDto?.industry ||
    [];
  const rawSkills = userProfile?.skills || [];
  const { industries, roles } = splitProfileIndustries(rawIndustry, rawSkills, {
    isBrand,
  });
  const primaryRole =
    roles.join(" · ") ||
    industries.slice(0, 2).join(" · ") ||
    (isBrand ? "Brand" : "Creative");

  const imagePath =
    userProfile?.companyResponseDto?.logo || userProfile?.profileUrl;
  const imageSrc = imagePath ? serverFiles(imagePath) : null;
  const filterItems = normalizeFilters(filters);

  return (
    <>
      <Link
        to="/user-profile"
        className="block rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6 text-center hover:border-zinc-700 transition-colors"
      >
        <div className="mx-auto h-24 w-24 rounded-full overflow-hidden ring-2 ring-zinc-800">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={displayName}
              className="h-full w-full object-cover"
              draggable={false}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = NO_IMAGE;
              }}
            />
          ) : (
            <div className="avatar-fallback h-full w-full flex items-center justify-center font-display font-bold text-2xl">
              {initialsOf(userProfile)}
            </div>
          )}
        </div>
        <div className="mt-4 font-display font-bold text-lg tracking-tight text-zinc-50">
          {displayName || "Your profile"}
        </div>
        <div className="mt-1 text-sm text-zinc-400">{primaryRole}</div>
        {location ? (
          <div className="mt-0.5 text-sm text-zinc-500">{location}</div>
        ) : null}
      </Link>

      {filterItems.length > 0 && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 font-semibold mb-3">
            <Filter size={12} className="text-[#8b83ff]" /> {filterTitle}
          </div>
          <div className="flex flex-col gap-1">
            {filterItems.map((f) => {
              const active =
                String(currentFilter).toUpperCase() ===
                String(f.value).toUpperCase();
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => onFilterChange?.(f.value)}
                  className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${
                    active
                      ? "bg-[#6C63F5]/15 text-[#8b83ff] font-medium"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <nav className="px-5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
        {FOOTER_LINKS.map((l) => (
          <button
            key={l.key}
            type="button"
            onClick={() => legal?.openPage(l.key)}
            className="hover:text-zinc-200 transition-colors"
          >
            {l.label}
          </button>
        ))}
      </nav>
      <div className="px-5 text-[0.7rem] text-zinc-600">
        © {new Date().getFullYear()} Qreativs
      </div>
    </>
  );
};

export default LeftSidebar;
